import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import H from '@here/maps-api-for-javascript';
import withTranslation from '../../../hoc/withTranslation';
import { fitBoundsWithOffset, enhanceGeoJSONFeatures, arePropsEqual } from './utils/markerHelpers';
import { updateGroupMarkers } from './utils/markerFactory';
import { attachHoverEvents, attachInteractionEvents } from './hooks/useMarkerEvents';
import { useMarkerCreation } from './hooks/useMarkerCreation';

const NewJobMarkers = ({ translations, map, geoJSON, ui, user, oAuth }) => {
  const dispatch = useDispatch();
  const solution = useSelector((state) => state.solution);
  const orders = useSelector((state) => state.orders[solution.show]);
  const display = useSelector((state) => state.mapContainer.display);
  const routingData = useSelector((state) => state.routingTour);

  const ordersRef = useRef(orders);
  const solutionRef = useRef(solution);
  const userRef = useRef(user);
  const oAuthRef = useRef(oAuth);
  const displayRef = useRef(display);
  const routingDataRef = useRef(routingData);
  const groupRef = useRef(null);
  const hasInitializedBoundsRef = useRef(false);
  const shouldRefitBoundsRef = useRef(false);

  useEffect(() => {
    ordersRef.current = orders;
    solutionRef.current = solution;
    userRef.current = user;
    oAuthRef.current = oAuth;
    displayRef.current = display;
    routingDataRef.current = routingData;
  });

  const { onMarkerDragEnd } = useMarkerCreation(
    dispatch,
    ordersRef,
    solutionRef,
    userRef,
    oAuthRef,
    shouldRefitBoundsRef,
  );

  useEffect(() => {
    if (map && !groupRef.current) {
      groupRef.current = new H.map.Group();
      map.addObject(groupRef.current);

      map.addEventListener(
        'drag',
        (ev) => {
          if (ev.target instanceof H.map.Marker && ev.target.offset) {
            ev.target.setGeometry(
              map.screenToGeo(
                ev.currentPointer.viewportX - ev.target.offset.x,
                ev.currentPointer.viewportY - ev.target.offset.y,
              ),
            );
          }
        },
        false,
      );
    }
    return () => {
      if (groupRef.current) {
        groupRef.current.removeAll();
        map.removeObject(groupRef.current);
        groupRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!groupRef.current) return;

    if (!display?.showProblem) {
      groupRef.current.removeAll();
      hasInitializedBoundsRef.current = false;
    }
  }, [display?.showProblem]);

  useEffect(() => {
    return () => {
      if (groupRef.current) {
        groupRef.current.removeAll();
      }
    };
  }, []);

  useEffect(() => {
    if (!groupRef.current || !ui.current) return;

    if (!geoJSON?.geo?.features || geoJSON.geo.features.length === 0) {
      groupRef.current.removeAll();
      hasInitializedBoundsRef.current = false;
      return;
    }

    const enhancedGeoJSON = enhanceGeoJSONFeatures(
      geoJSON,
      ordersRef.current,
      displayRef.current,
      userRef.current,
      routingDataRef.current,
    );

    if (!enhancedGeoJSON?.geo?.features || enhancedGeoJSON.geo.features.length === 0) {
      groupRef.current.removeAll();
      hasInitializedBoundsRef.current = false;
      return;
    }

    updateGroupMarkers(
      enhancedGeoJSON,
      groupRef.current,
      ui.current,
      translations,
      map,
      dispatch,
      ordersRef,
      solution,
      onMarkerDragEnd,
      attachHoverEvents,
      attachInteractionEvents,
    );

    if (groupRef.current.getObjects().length > 0) {
      if (!hasInitializedBoundsRef.current) {
        fitBoundsWithOffset(map, groupRef.current.getBoundingBox());
        hasInitializedBoundsRef.current = true;
      } else if (shouldRefitBoundsRef.current) {
        fitBoundsWithOffset(map, groupRef.current.getBoundingBox());
        shouldRefitBoundsRef.current = false;
      }
    }
  }, [geoJSON, map, translations, ui, onMarkerDragEnd, dispatch, solution, display, routingData]);

  return null;
};

const MemoizedNewJobMarkers = React.memo(NewJobMarkers, arePropsEqual);

export default withTranslation(MemoizedNewJobMarkers);
